import {makeAutoObservable} from 'mobx';
import moment from 'moment';
import _ from 'lodash';
import {api, t} from 'shared/core';
import {createStore, DomainStore} from 'shared/store';
import {errorAlert} from 'shared/tools';
import {endpoints, types} from 'main/tools';
import {Shift, TimeTrackingDimension, TimeTrackingSchedule} from 'main/stores/time_tracking';

class ShiftSchedulerState {
  store = new DomainStore();
  schedule = null;
  dimensions = [];
  weekStart = currentWeekStart();
  latestRequestId = 0;
  isUpdating = false;

  editingShift = new Shift();
  originalShift = null;
  errors = {};
  repeatScopeRequest = {};
  publishRequest = {};
  publishedShiftChangeRequest = {};
  copyingFromEmployee = null;
  copyErrors = {};

  copyOnDrop = false;
  hoveredCell = null;

  constructor() {
    makeAutoObservable(this, {store: false});
  }

  async load() {
    await this.store._compose([endpoints.TIME_TRACKING.DIMENSIONS.ALL]);
    this.dimensions = this.store._getAll(types.TIME_TRACKING.DIMENSION, TimeTrackingDimension);

    await this.loadSchedule();
  }

  async loadSchedule() {
    const store = new DomainStore();
    const params = {
      start_date: this.weekStart.format('YYYY-MM-DD'),
      end_date: this.weekEnd.format('YYYY-MM-DD')
    };

    await this.latestRequest(
      () => store._compose([{url: endpoints.TIME_TRACKING.SCHEDULE, params}]),
      () => {
        this.schedule = new TimeTrackingSchedule(store._getSingle(types.TIME_TRACKING.SCHEDULE));
      }
    );
  }

  get sortedDimensions() {
    return _.sortBy(this.dimensions, 'order');
  }

  get days() {
    return _.range(7).map(offset => this.weekStart.clone().add(offset, 'days'));
  }

  get weekEnd() {
    return this.weekStart.clone().add(6, 'days');
  }

  get weekRange() {
    return t('components.dates.DATE_SPAN', {
      startDate: this.weekStart.format('MMM D'),
      endDate: this.weekEnd.format('MMM D, YYYY')
    });
  }

  goToPreviousWeek() {
    this.weekStart = this.weekStart.clone().subtract(1, 'week');
    this.loadSchedule();
  }

  goToNextWeek() {
    this.weekStart = this.weekStart.clone().add(1, 'week');
    this.loadSchedule();
  }

  async goToCurrentWeek() {
    this.weekStart = currentWeekStart();
    await this.loadSchedule();
  }

  newShift(employee, day) {
    this.openEditShiftModal({employee, date: day.toDate()});
  }

  openEditShiftModal(shift) {
    this.originalShift = shift;
  }

  get scheduleRuleChanged() {
    return this.editingShift.scheduleRuleDiffersFrom(this.originalShift);
  }

  get needsPropagateValue() {
    return !!this.originalShift.scheduleRule && !this.scheduleRuleChanged && this.editingShift.differsFrom(this.originalShift);
  }

  async confirmPublishedShiftChange() {
    const {action, shift, employee, day} = this.publishedShiftChangeRequest;
    this.closePublishedShiftChangeModal();

    if (action === 'delete') {
      await this.deleteShift(shift);
    } else {
      await this.dropShift(shift.id, employee, day, true);
    }
  }

  async saveShift(propagate) {
    const shift = this.editingShift;
    const shiftStore = new DomainStore();
    const dryRun = propagate === undefined && this.needsPropagateValue;
    const propagateChanges = dryRun || (propagate ?? this.scheduleRuleChanged);

    const {errors} = await this.latestRequest(
      () => shift.isNew ?
        shiftStore.post(endpoints.TIME_TRACKING.SHIFTS.ALL, types.TIME_TRACKING.SHIFT, shift) :
        shiftStore.patch(`${endpoints.TIME_TRACKING.SHIFTS.with(shift.id)}?propagate=${propagateChanges}&dry_run=${dryRun}`, types.TIME_TRACKING.SHIFT, shift),
      ({errors}) => _.isEmpty(errors) && !dryRun && this.schedule.replaceShiftsFor([shift.employeeId], shiftStore._getAll(types.TIME_TRACKING.SHIFT, Shift))
    );

    this.errors = errors;
    if (!_.isEmpty(errors)) return;

    if (dryRun) {
      this.openRepeatScopeModal({action: 'update', shift, scope: 'this'});
    } else {
      this.closeEditShiftModal();
    }
  }

  requestDeleteShift(shift) {
    if (shift.scheduleRule) {
      this.openRepeatScopeModal({action: 'delete', shift, scope: 'this'});
    } else if (shift.published) {
      this.openPublishedShiftChangeModal({action: 'delete', shift});
    } else {
      this.deleteShift(shift);
    }
  }

  async deleteShift(shift, scope) {
    const shiftStore = new DomainStore();

    await this.latestRequest(
      () => shiftStore.destroy(types.TIME_TRACKING.SHIFT, shift, {propagate: scope === 'future'}),
      ({errors}) => _.isEmpty(errors) && this.schedule.replaceShiftsFor([shift.employeeId], shiftStore._getAll(types.TIME_TRACKING.SHIFT, Shift))
    );
  }

  setRepeatScope(scope) {
    this.repeatScopeRequest.scope = scope;
  }

  async confirmRepeatScope() {
    const {action, shift, scope} = this.repeatScopeRequest;

    if (action === 'update') {
      await this.saveShift(scope === 'future');
    } else {
      await this.deleteShift(shift, scope);
    }

    this.closeRepeatScopeModal();
  }

  async latestRequest(request, applyResponse) {
    const requestId = ++this.latestRequestId;
    this.isUpdating = true;

    try {
      const response = await request();
      if (this.latestRequestId === requestId) applyResponse(response);

      return response;
    } finally {
      if (this.latestRequestId === requestId) this.isUpdating = false;
    }
  }

  async dropShift(shiftId, employee, day, publishedChangeConfirmed = false) {
    const shift = _.find(this.schedule.shifts, {id: shiftId});
    const employeeId = employee.id;
    if (!this.copyOnDrop && shift.employeeId === employeeId && day.isSame(shift.date, 'day')) return;

    if (!this.copyOnDrop && shift.published && !publishedChangeConfirmed) {
      this.openPublishedShiftChangeModal({action: 'drop', shift, employee, day});
      return;
    }

    const changes = {employee, date: day.toDate()};
    const shiftStore = new DomainStore();
    const request = this.copyOnDrop ?
      () => shiftStore.post(endpoints.TIME_TRACKING.SHIFTS.ALL, types.TIME_TRACKING.SHIFT, shift.copyWith({...changes, id: null, scheduleRule: null})) :
      () => shiftStore.patch(endpoints.TIME_TRACKING.SHIFTS.with(shift.id), types.TIME_TRACKING.SHIFT, shift.copyWith(changes));

    const {errors} = await this.latestRequest(
      request,
      ({errors}) => _.isEmpty(errors) && this.schedule.replaceShiftsFor([employeeId], shiftStore._getAll(types.TIME_TRACKING.SHIFT, Shift))
    );
    if (!_.isEmpty(errors)) errorAlert(_.values(errors).join(' '));
  }

  endShiftDrag(shiftId, dropped) {
    // There's a bug in Chromium browsers that make it so they sometimes don't fire the drop event when modifier keys are held.
    // This ensures that it triggers anyway, but only if the drop command did not fire or else we'd end up with two copies.
    if (!dropped && this.copyOnDrop && this.hoveredCell) {
      this.dropShift(shiftId, this.hoveredCell.employee, this.hoveredCell.day);
    }

    this.copyOnDrop = false;
    this.hoveredCell = null;
  }

  employeeActionLinks(employee) {
    return _.compact([
      this.schedule.hasLink('publish') && this.schedule.unpublishedShiftsFor(employee.id).length > 0 && {
        text: 'time_tracking.shifts.Publish Shifts',
        action: () => this.requestPublish(employee)
      },
      this.schedule.shiftsByEmployee[employee.id] && {
        text: 'time_tracking.shifts.Copy Shifts to Employees',
        action: () => this.openCopyEmployeeShiftsModal(employee)
      },
      {
        text: 'time_tracking.shifts.Copy Previous Week',
        action: () => this.copyPreviousWeek(employee.id)
      }
    ]);
  }

  async requestWeekShifts(request, employeeIds) {
    try {
      await this.latestRequest(request, response => {
        const store = createStore();
        store.sync(response.data);
        this.schedule.replaceShiftsFor(employeeIds, store.getAll(types.TIME_TRACKING.SHIFT).map(shift => new Shift(shift)));
      });
      return {};
    } catch (error) {
      if (!error.formErrors) throw error;

      return {errors: error.formErrors};
    }
  }

  requestPublish(employee = null) {
    const shifts = employee ? this.schedule.unpublishedShiftsFor(employee.id) : this.schedule.unpublishedShifts;

    this.openPublishConfirmationModal({employee, count: shifts.length});
  }

  async confirmPublish() {
    const {employee} = this.publishRequest;
    this.closePublishConfirmationModal();

    await this.publishWeek(employee?.id);
  }

  async publishWeek(employeeId = null) {
    const payload = {start_date: this.weekStart.format('YYYY-MM-DD')};
    if (employeeId) payload.employee_id = employeeId;

    const {errors} = await this.requestWeekShifts(() => api.patch(this.schedule.link('publish'), payload), [employeeId]);
    if (errors) errorAlert(_.values(errors).join(' '));
  }

  async copyPreviousWeek(employeeId = null) {
    const payload = {start_date: this.weekStart.format('YYYY-MM-DD')};
    if (employeeId) payload.employee_id = employeeId;

    const {errors} = await this.requestWeekShifts(() => api.post(endpoints.TIME_TRACKING.SHIFTS.COPY_PREVIOUS_WEEK, payload), [employeeId]);
    if (errors) errorAlert(_.values(errors).join(' '));
  }

  get copyableEmployees() {
    return this.schedule.employees.filter(employee => employee.id !== this.copyingFromEmployee?.id);
  }

  get copyingFromEmployeeShifts() {
    return this.schedule.shiftsByEmployee[this.copyingFromEmployee?.id] || [];
  }

  async copyEmployeeShifts(targetEmployees) {
    const targetEmployeeIds = _.map(targetEmployees, 'id');
    const {errors} = await this.requestWeekShifts(
      () => api.post(endpoints.TIME_TRACKING.SHIFTS.COPY, {
        employee_id: this.copyingFromEmployee.id,
        employee_ids: targetEmployeeIds,
        start_date: this.weekStart.format('YYYY-MM-DD')
      }),
      targetEmployeeIds
    );

    this.copyErrors = errors || {};
    if (!errors) this.closeCopyEmployeeShiftsModal();
  }
}

function currentWeekStart() {
  return moment().day(0).startOf('day');
}

export default ShiftSchedulerState;
